import SectionPanel from '@/components/SectionPanel';
import { Badge, Flex, Grid, GridItem, HStack, SegmentGroup, Stack, useDisclosure } from "@chakra-ui/react";
import Content from "src/components/Content"
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
} from "@chakra-ui/react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, isWithinInterval, isToday } from 'date-fns';
import React from "react";
import { de } from 'date-fns/locale';
import MonthNavigation from "./components/MonthNavigation";
import { useAuthContext, useOrganization } from "src/auth/AuthContext";
import WaitingSpinner from "@/components/WaitingSpinner";
import { Avatar } from "@/components/ui/avatar"
import AbsenceDialog from "./AbsenceDialog";
import { Holiday } from "@/api/Holiday";
import { deLocaleOption, formatDay, formatIso8601, localTimezone } from "@/data/DateTimeUtil";
import { User } from "@/api/User";
import { Absence } from "@/api/Absence";
import { VacationInhabitant } from "@/api/Vacation";
import { PendingInhabitant } from "@/api/Pending";
import { ApprovedInhabitant } from "@/api/Approved";
import { RejectedInhabitant } from "@/api/Rejected";
import { absenceService } from "@/service/AbsenceService";
import { holidayService } from "@/service/HolidayService";
import { userService } from "@/service/UserService";
import { LuUser, LuUsers } from "react-icons/lu";
import AddButton from "@/components/buttons/AddButton";
import { ContractSummary } from "@/api/ContractSummary";
import { workmodelService } from "@/service/WorkModelService";
import ContractTabs from "@/components/ContractTabs";
import { ApprovalStatusUtil } from "@/data/ApprovalStatusUtil";
import { OrgUtil } from "@/data/OrgUtil";
import { summarizeAbsencePendingDays } from "@/data/ApprovalSummaryUtil";
import { useI18n } from '@/i18n/I18nContext';
import { HolidayDetails } from './components/HolidayDetails';
import { usePageRefresh } from '@/hooks/usePageRefresh';

type AbsenceCalendarProps = {
  userId?: number;
  initialMonth?: Date;
  active?: boolean;
};

export const AbsenceCalendar = ({ userId: targetUserId, initialMonth, active = true }: AbsenceCalendarProps) => {
  const { user } = useAuthContext();
  const organization = useOrganization();
  const revision = usePageRefresh();
  const token = user?.token?.trim();
  const isOrganizationLoading = !!token && !organization;
  const organizationId = organization?.id;
  const country = organization?.country?.trim().toUpperCase() || "CH";
  const city = organization?.city?.trim() || undefined;

  const now = new Date();
  const emptyAbsenceEntry: Absence = {
    id: -1,
    localStartDate: formatIso8601(now),
    localEndDate: formatIso8601(now),
    timezone: localTimezone,
    days: isWeekend(now) ? 0 : 1,
    halfDay: false,
    absenceType: VacationInhabitant,
    status: PendingInhabitant,
    comments: undefined,
    userId: user?.data?.id,
    organizationId: user?.data?.organizationId,
    decidedBy: null,
    decidedAt: null,
    decisionReason: null
  };
  const [editData, setEditData] = useState<Absence>(emptyAbsenceEntry);
  const { open: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(initialMonth ?? new Date());
  const [holidayMap, setHolidayMap] = useState<Map<string, Holiday>>(new Map());
  // userId -> absences
  const [absenceMap, setAbsenceMap] = useState<Map<number, Absence[]>>(new Map());
  const [users, setUsers] = useState<User[]>([]);
  const [reportsByManager, setReportsByManager] = useState<Map<number, number[]>>(new Map());
  // FE switch
  const [showAll, setShowAll] = useState(true);
  // summary tab
  const [contract, setContract] = useState<ContractSummary | null>(null);

  const role = user?.data?.role;
  const currentUserId = user?.data?.id;
  const isCurrentUserAdmin = role ? OrgUtil.isRoleAdmin(role) || OrgUtil.isRoleRoot(role) : false;
  const isCalendarLoading = isLoading || isOrganizationLoading;

  useEffect(() => {
    setCurrentMonth(current => {
      const next = initialMonth ?? new Date();
      return formatIso8601(current) === formatIso8601(next) ? current : next;
    });
  }, [initialMonth, targetUserId]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(!!token);
    setError(null);
    setHolidayMap(new Map());
    setAbsenceMap(new Map());
    setUsers([]);
    setContract(null);
    setReportsByManager(new Map());
    if (!token || isOrganizationLoading || !active) return;

    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const p1 = holidayService.holidays(currentMonth.getFullYear(), country, "DE", city);
    const p2 = absenceService.findAllAbsences(currentMonth.getFullYear(), currentMonth.getMonth() + 1);

    const summaryUserId = targetUserId ?? currentUserId;
    const p3 = summaryUserId ? workmodelService.contractSummary(summaryUserId) : Promise.resolve(null);
    const p4 = userService.listReports()
      .then(r => userService.toNumberArrayMap(
        r.reports as Readonly<Map<number, ReadonlyArray<number>>> | Record<string, number[]>
      ))
      .catch(() => new Map<number, number[]>());
    const p5 = currentUserId
      ? absenceService.findEntries(currentUserId, formatIso8601(monthStart), formatIso8601(monthEnd))
      : Promise.resolve<Absence[]>([]);

    Promise.all([p1, p2, p3, p4, p5])
      .then(([holidays, orgAbsences, contractSummary, reports, currentUserAbsences]) => {
        if (!isActive) return;
        setHolidayMap(new Map(holidays.map(h => [formatIso8601(h.date), h])));

        const nextAbsenceMap = new Map(
          Array.from(orgAbsences.entries(), ([user, absences]) => [user.id, absences])
        );
        if (currentUserId) {
          const ownAbsencesById = new Map((nextAbsenceMap.get(currentUserId) ?? []).map(absence => [absence.id, absence]));
          currentUserAbsences.forEach(absence => ownAbsencesById.set(absence.id, absence));
          nextAbsenceMap.set(currentUserId, Array.from(ownAbsencesById.values()));
        }
        setAbsenceMap(nextAbsenceMap);

        const loadedUsers = Array.from(orgAbsences.keys()).sort((a, b) => a.firstName.localeCompare(b.firstName));
        setUsers(targetUserId ? loadedUsers.filter(u => u.id === targetUserId) : loadedUsers);
        setContract(contractSummary);
        setReportsByManager(reports);
      })
      .catch(() => {
        if (isActive) setError("Abwesenheitskalender konnte nicht geladen werden.");
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => { isActive = false; };
  }, [currentMonth, currentUserId, targetUserId, token, organizationId, country, city, isOrganizationLoading, revision, active]);

  const summaryUserId = targetUserId ?? currentUserId;
  const pendingAbsenceDays = useMemo(() =>
    summaryUserId ? summarizeAbsencePendingDays(absenceMap.get(summaryUserId) ?? []) : 0
  , [absenceMap, summaryUserId]);

  const canApproveEntry = (entry?: Absence): boolean => {
    if (!entry || entry.id <= 0 || !ApprovalStatusUtil.isPending(entry.status)) return false;
    if (isCurrentUserAdmin) return true;
    if (!currentUserId || entry.userId === currentUserId) return false;
    return (reportsByManager.get(currentUserId) ?? []).includes(entry.userId);
  };

  const canRejectEntry = (entry?: Absence): boolean => canApproveEntry(entry);

  const canEditEntry = (entry?: Absence): boolean =>
    !!entry &&
    entry.userId === currentUserId &&
    (ApprovalStatusUtil.isPending(entry.status) || ApprovalStatusUtil.isRejected(entry.status));

  const canCancelEntry = (entry?: Absence): boolean =>
    !!entry && entry.id > 0 && !ApprovalStatusUtil.isCancelled(entry.status) && (entry.userId === currentUserId || isCurrentUserAdmin);

  const saveEntry = (data: Absence) => {
    onEditClose();
    console.log(`saving entry ${JSON.stringify(data)}`);
    // update id for new entries, save it first to BE
    const newEntry = data.id < 0;
    if (newEntry) {
      absenceService.addAbsence(data).then(id => {
        data.id = id;
        const user = absenceMap.get(data.userId);
        if (user) {
          user.push(data);
        } else {
          absenceMap.set(data.userId, [data]);
        }
        setAbsenceMap(new Map(absenceMap));
      });
    } else {
      const resubmittedEntry = ApprovalStatusUtil.isRejected(data.status);
      const savePromise = resubmittedEntry
        ? absenceService.resubmitAbsence(data)
        : absenceService.updateAbsence(data);

      savePromise.then(id => {
        const user = absenceMap.get(data.userId);
        if (user) {
          const index = user.findIndex(e => e.id === data.id);
          if (index >= 0) {
            user[index] = resubmittedEntry
              ? {
                ...data,
                id,
                status: PendingInhabitant,
                decidedBy: null,
                decidedAt: null,
                decisionReason: null,
              }
              : data;
          }
          setAbsenceMap(new Map(absenceMap));
        }
      });
    }
  }

  const deleteEntry = (data: Absence) => {
    onEditClose();
    console.log(`deleting entry ${JSON.stringify(data)}`);
    absenceService.deleteAbsence(data.userId, data.id).then(_id => {
      const user = absenceMap.get(data.userId);
      if (user) {
        const index = user.findIndex(e => e.id === data.id);
        if (index >= 0) {
          user.splice(index, 1);
        }
        setAbsenceMap(new Map(absenceMap));
      }
    });
  }

  const rejectEntry = (data: Absence, reason: string) => {
    onEditClose();
    const trimmedReason = reason.trim();
    absenceService.rejectAbsence(data.userId, data.id, trimmedReason).then(_id => {
      const entries = absenceMap.get(data.userId);
      if (entries) {
        const index = entries.findIndex(e => e.id === data.id);
        if (index >= 0) {
          entries[index] = {
            ...data,
            status: RejectedInhabitant,
            decidedBy: currentUserId ?? null,
            decidedAt: new Date(),
            decisionReason: trimmedReason,
          };
        }
        setAbsenceMap(new Map(absenceMap));
      }
    });
  }

  const approveEntry = (data: Absence) => {
    onEditClose();
    console.log(`approving entry ${JSON.stringify(data)}`);
    absenceService.approveAbsence(data.userId, data.id).then(_id => {
      const entries = absenceMap.get(data.userId);
      if (entries) {
        const index = entries.findIndex(e => e.id === data.id);
        if (index >= 0) {
          entries[index] = {
            ...data,
            status: ApprovedInhabitant,
            decidedBy: currentUserId ?? null,
            decidedAt: new Date(),
            decisionReason: null,
          };
        }
        setAbsenceMap(new Map(absenceMap));
      }
    });
  }

  const newEntry = (day: Date) => {
    console.info('new absence')
    setEditData({
      ...emptyAbsenceEntry,
      localStartDate: formatIso8601(day),
      localEndDate: formatIso8601(day),
      timezone: localTimezone,
      userId: user?.data?.id,
      organizationId: user?.data?.organizationId,
      status: PendingInhabitant,
      decidedBy: null,
      decidedAt: null,
      decisionReason: null
    })
    onEditOpen();
  }
  const updateEntry = (entry: Absence) => {
    console.info(`updating ${entry.id}`)
    setEditData(entry);
    onEditOpen();
  }

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const renderCells = () => {
    return (
      <Grid templateColumns={`12rem repeat(${days.length}, minmax(2.5rem, 1fr))`} gap={1} minW='max-content'>
        {/* Header Row */}
        <GridItem colSpan={1} bg='bg.emphasized' p={2} borderRadius='md'>
          <Text fontWeight="bold">Mitarbeitende</Text>
        </GridItem>
        {days.map((day, index) => {
          const today = isToday(day);
          const weekend = isWeekend(day);
          const holiday: Holiday | null = holidayMap.get(formatIso8601(day));
          return (
            <GridItem key={index} bg='bg.emphasized' p={2} textAlign='center' borderRadius='md'>
              <Text color={today ? 'red.fg' : weekend || holiday ? 'green.fg' : 'fg'} fontWeight="bold">{format(day, 'd')}</Text>
              <Text color={weekend || holiday ? 'green.fg' : 'fg'} fontSize="xs">{format(day, 'EE', deLocaleOption)}</Text>
              {holiday && <HolidayDetails holiday={holiday} compact />}
            </GridItem>
          );
        })}

        {/* User Rows */}
        {users.filter(u => targetUserId ? u.id === targetUserId : (showAll || user?.data?.id === u.id)).map((employee, userIndex) => {
          const isLoggedIn = user?.data?.id === employee.id;
          const absences = absenceMap.get(employee.id) ?? [];
          const pendingDays = summarizeAbsencePendingDays(absences);
          const name = `${employee.firstName} ${employee.lastName}`;
          return (
            <React.Fragment key={userIndex}>
              <GridItem colSpan={1} bg='bg.muted' p={2}>
                <Flex gap='2' align='center' minW='0'>
                  <Avatar
                    size='sm'
                    borderless={false}
                    borderWidth='1px'
                    shadow='sm'
                    name={name}
                    src={userService.avatarUrl(employee)}
                  />
                  <Text flex='1' minW='0' overflowWrap='anywhere' fontWeight='semibold'>{name}</Text>
                </Flex>
                {pendingDays > 0 && (
                  <Badge colorPalette='purple' variant='subtle' mt='2'>
                    {formatDay(pendingDays)} Tg offen
                  </Badge>
                )}
              </GridItem>
              {days.map((day, dayIndex) => {
                const isoDay = formatIso8601(day);
                const weekend = isWeekend(day);
                const holiday: Holiday | null = holidayMap.get(isoDay);
                const absenceEntry = absences.find(range => isWithinInterval(isoDay, { start: range.localStartDate, end: range.localEndDate }));
                const isAbsence = absenceEntry !== undefined;
                const canOpenAbsence = isAbsence && (canEditEntry(absenceEntry) || canApproveEntry(absenceEntry) || canCancelEntry(absenceEntry));
                const canCreateAbsence = isLoggedIn && !isAbsence;
                const isApprovedAbsence = isAbsence && ApprovalStatusUtil.isApproved(absenceEntry.status);
                const isRejectedAbsence = isAbsence && ApprovalStatusUtil.isRejected(absenceEntry.status);
                return (
                  <GridItem
                    key={dayIndex}
                    bg={isAbsence ? (isApprovedAbsence ? 'green.400' : isRejectedAbsence ? 'orange.300' : 'yellow.300') : 'bg.subtle'}
                    color={isAbsence ? 'gray.950' : 'fg'}
                    borderRadius={isAbsence || weekend ? 'md' : 'none'}
                    boxShadow={isAbsence ? 'md' : 'none'}
                    p={2}
                    textAlign='center'
                    backgroundImage={
                      weekend
                        ? "repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(0,0,0,0.05) 5px, rgba(0,0,0,0.05) 10px)"
                        : (holiday ? 'repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(0,200,0,0.2) 5px, rgba(0,200,0,0.2) 10px)'
                          : "none")
                    }
                    _hover={canOpenAbsence || canCreateAbsence ? {
                      bgColor: 'blue.600',
                      color: 'white',
                      boxShadow: 'lg',
                      borderRadius: 'md',
                      cursor: 'pointer'
                    } : {}}
                    onClick={() => {
                      if (isAbsence && canOpenAbsence) {
                        updateEntry(absenceEntry);
                      } else if (canCreateAbsence) {
                        newEntry(day);
                      }
                    }}
                  >
                    {isAbsence && (
                      <Flex h="100%" align="center" justify="center">
                        <Text fontWeight='bold'>{isApprovedAbsence ? '✓' : isRejectedAbsence ? '!' : '…'}</Text>
                      </Flex>
                    )}
                  </GridItem>
                );
              })}
            </React.Fragment>
          )
        })}
      </Grid>
    );
  }

  return (
    <Stack gap='6' minW='0'>
      <ContractTabs
        summary={isCalendarLoading ? null : contract}
        monthMinutes={isCalendarLoading ? null : contract?.workedMinutesThisMonth}
        month={currentMonth}
        approvalSummary={{ absenceDays: isCalendarLoading ? 0 : pendingAbsenceDays }}
      />
      <SectionPanel>
        <Flex alignItems='center' gap={4} flexWrap='wrap'>
          <MonthNavigation currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} language={de} />
          {!targetUserId && <AddButton caption='Neuer Eintrag' disabled={isCalendarLoading || !token || !!error} onClick={() => newEntry(new Date())} />}
          {isCalendarLoading && (
            <WaitingSpinner role='status' aria-label='Abwesenheitskalender wird geladen' size='lg' />
          )}
        </Flex>
        {/* when is extracted in a separate component, then the animation doesn't work */}
        {!targetUserId && (
          <SegmentGroup.Root aria-label='Mitarbeitende anzeigen' alignSelf='flex-start' maxW='full'
            value={showAll.toString()} onValueChange={(e) => setShowAll(e.value === "true")}
          >
            <SegmentGroup.Indicator />
            <SegmentGroup.Items items={[
              {
                value: 'false',
                label: (<HStack><LuUser aria-hidden='true' />Ich</HStack>),
              },
              {
                value: 'true',
                label: (<HStack><LuUsers aria-hidden='true' />Alle Mitarbeitende</HStack>),
              },
            ]} />
          </SegmentGroup.Root>
        )}
        {error && <Text role='alert' color='red.fg'>{error}</Text>}
        {!isCalendarLoading && !error && <Box overflowX='auto' minW='0'>{renderCells()}</Box>}
      </SectionPanel>
      <AbsenceDialog
        isOpen={isEditOpen}
        onClose={onEditClose}
        entry={editData}
        canEdit={canEditEntry(editData)}
        canApprove={canApproveEntry(editData)}
        canReject={canRejectEntry(editData)}
        canCancel={canCancelEntry(editData)}
        onSave={saveEntry}
        onDelete={deleteEntry}
        onApprove={approveEntry}
        onReject={rejectEntry}
      />
    </Stack>
  );
};

const OverviewAbsence = () => {
  const { t } = useI18n();
  return (
    <Content title={t('workspaces.absences.title')} description={t('workspaces.absences.description')}>
      <AbsenceCalendar />
    </Content>
  );
}

export default OverviewAbsence;
